import { Plus, X, CopyCheck } from "lucide-react";
import { fmtSlots } from "../lib/format";

const MAX_DAYS = 14;
const TZ = "Europe/Copenhagen";
const pad = (n) => String(n).padStart(2, "0");
const parts = (ms) =>
  Object.fromEntries(
    new Intl.DateTimeFormat("en-CA", { timeZone: TZ, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", hourCycle: "h23" })
      .formatToParts(new Date(ms))
      .map((x) => [x.type, x.value])
  );
const cphDate = (iso) => {
  const p = parts(Date.parse(iso));
  return `${p.year}-${p.month}-${p.day}`;
};
const cphTime = (iso) => {
  const p = parts(Date.parse(iso));
  return `${p.hour}:${p.minute}`;
};
// Copenhagen wall-clock date + time → UTC ISO, independent of the browser's time zone
function cphIso(date, time) {
  const [y, m, d] = date.split("-").map(Number);
  const [h, mi] = time.split(":").map(Number);
  const want = Date.UTC(y, m - 1, d, h, mi);
  let t = want;
  for (let i = 0; i < 2; i++) {
    const p = parts(t);
    t += want - Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour, +p.minute);
  }
  return new Date(t).toISOString();
}
const localDate = (d) => cphDate(d.toISOString());

export const slotsToRows = (slots) => slots.map((s) => ({ date: cphDate(s.start), from: cphTime(s.start), to: cphTime(s.end) }));

export const rowsToSlots = (rows) =>
  rows
    .filter((r) => r.date && r.from && r.to)
    .map((r) => ({ start: cphIso(r.date, r.from), end: cphIso(r.date, r.to) }))
    .sort((a, b) => a.start.localeCompare(b.start));

export function rowsError(rows) {
  if (!rows.length || rows.some((r) => !r.date || !r.from || !r.to)) return "Udfyld dato, fra og til for hver afhentningsdag.";
  if (rows.some((r) => r.to <= r.from)) return "Afhentning \"til\" skal være efter \"fra\" på hver dag.";
  const slots = rowsToSlots(rows);
  for (let i = 1; i < slots.length; i++) if (slots[i].start < slots[i - 1].end) return "Afhentningsdagene må ikke overlappe.";
  if ((Date.parse(slots[slots.length - 1].start) - Date.parse(slots[0].start)) / 864e5 >= MAX_DAYS) return `Afhentningen kan højst strække sig over ${MAX_DAYS} dage.`;
  return null;
}

export const defaultRows = () => [{ date: nextDay(localDate(new Date())), from: "14:00", to: "18:00" }];

function nextDay(date) {
  const [y, m, d] = date.split("-").map(Number);
  const t = new Date(Date.UTC(y, m - 1, d + 1));
  return `${t.getUTCFullYear()}-${pad(t.getUTCMonth() + 1)}-${pad(t.getUTCDate())}`;
}

export default function SlotEditor({ rows, onChange }) {
  const setRow = (i, k) => (e) => onChange(rows.map((r, j) => (j === i ? { ...r, [k]: e.target.value } : r)));
  const add = () => {
    const last = rows[rows.length - 1] || { date: localDate(new Date()), from: "10:00", to: "18:00" };
    onChange([...rows, { ...last, date: nextDay(last.date) }]);
  };
  const remove = (i) => onChange(rows.filter((_, j) => j !== i));
  const sameTimes = () => onChange(rows.map((r) => ({ ...r, from: rows[0].from, to: rows[0].to })));
  const err = rowsError(rows);
  return (
    <div className={`slots${rows.length === 1 ? " single" : ""}`} data-testid="pickup-slots">
      {rows.map((r, i) => (
        <div className="slot-row" key={i} data-testid={`slot-row-${i}`}>
          <div className="field">
            <label htmlFor={`slot-date-${i}`}>{i === 0 ? "Dato" : `Dag ${i + 1}`}</label>
            <input id={`slot-date-${i}`} type="date" required value={r.date} onChange={setRow(i, "date")} data-testid={`slot-date-${i}`} />
          </div>
          <div className="field">
            <label htmlFor={`slot-from-${i}`}>Fra</label>
            <input id={`slot-from-${i}`} type="time" required value={r.from} onChange={setRow(i, "from")} data-testid={`slot-from-${i}`} />
          </div>
          <div className="field">
            <label htmlFor={`slot-to-${i}`}>Til</label>
            <input id={`slot-to-${i}`} type="time" required value={r.to} onChange={setRow(i, "to")} data-testid={`slot-to-${i}`} />
          </div>
          {rows.length > 1 && (
            <button type="button" className="slot-remove" aria-label="Fjern dag" onClick={() => remove(i)} data-testid={`slot-remove-${i}`}>
              <X size={16} />
            </button>
          )}
        </div>
      ))}
      <div className="slot-actions">
        {rows.length < MAX_DAYS && (
          <button type="button" className="btn small" onClick={add} data-testid="slot-add">
            <Plus size={15} /> Tilføj dag
          </button>
        )}
        {rows.length > 1 && (
          <button type="button" className="btn small" onClick={sameTimes} data-testid="slot-same-times">
            <CopyCheck size={15} /> Samme tidspunkt alle dage
          </button>
        )}
      </div>
      {err ? (
        <p className="slot-error" data-testid="slot-error">{err}</p>
      ) : (
        <p className="hint" data-testid="slot-preview">Kunden ser: «Afhentning: {fmtSlots(rowsToSlots(rows))}»</p>
      )}
    </div>
  );
}
